import axios from "axios";
import getReCaptchaHeaderTBR from "@/utils/recaptchaTBR";
import { I_AM_A_TEAPOT, SERVICE_URL, SLOTS_HOLD_DELETE } from '@/store/_commons'
import { zonedTimeToUtc } from "date-fns-tz";
import { isAfter, isEqual } from "date-fns";
import appRouter from "@/router";

// ----------------------------
// Reservations

const namespaced = true;

const state = {};

const mutations = {};

const actions = {
  /**
   * Creates a reservation.
   * @param dispatch
   * @param rootState
   * @param data See API for required properties.
   * @returns {Promise<void>}
   */
  async createReservation({ dispatch, rootState }, data) {
    const endpoint = `${SERVICE_URL}reservation`;
    const config = await getReCaptchaHeaderTBR('post', endpoint);

    let args = {
        ...data,
    };

    const customerHasId = rootState.user?.customerDetail?.customerId;

    const reservationRequestPayload = {
      ...rootState.user.resDetails.userInfo,
      reservationSlotId: rootState.user.reservationSlotId,
      reservationUserIdentifier: rootState.user.reservationUserId,
      visitTypeId: rootState.user.resType,               
      isFeeApplicable: rootState.user.resFeeApplicable,
      onlineReservationFeeDetail: {
       ...(data.onlineReservationCreateReservationRequest?.onlineReservationFeeDetail || {}), 
       }
    };

    if (customerHasId) {
      reservationRequestPayload.customerId = rootState.user.customerDetail.customerId || '';
    }
    
    // Assign the completed request payload to the args object
    args.onlineReservationCreateReservationRequest = reservationRequestPayload;

    // payment would have been processed before reservation creation
    try {
      const result = await axios.post(endpoint, args, config);

      dispatch('setFinalReservationPropsHelper', result.data.onlineReservationCreateReservationResponse);
      // store payment receipt data in session
      data.onlineReservationCreateReservationRequest.onlineReservationFeeDetail.authorizationNumber = result.data.authorizationNumber;
      dispatch('setPaymentReceiptHelper', data.onlineReservationCreateReservationRequest.onlineReservationFeeDetail);
    } catch (error) {
      console.error('createReservation...', error);
      // Optional: re-throw error for handling in components
      // throw error; 
    }
  },


  /**
   * Reads (retrieves) a list of reservations based on user's email, phone number and/or
   * reservation confirmation number.
   * @param context
   * @param data
   * @returns {Promise<*|*[]>}
   */
  async findReservation(context, data) {
    let response = undefined;
    try {
      const endpoint = `${SERVICE_URL}reservation/getreservation`;
      const config = await getReCaptchaHeaderTBR('post', endpoint);
      response = await axios.post(endpoint, data, config);
    } catch (error) {
      console.error('findReservation...', error);
      // Optional: re-throw error for handling in components
      // throw error;
    }

    return response?.data.reservationDetailCollection ?? [];
  },

  /**
   * Updates an existing reservation.
   * @param dispatch
   * @param rootState
   * @returns {Promise<void>}
   */
  async updateReservation({ dispatch, rootState }) {
    try {
      const endpoint = `${SERVICE_URL}reservation`;
      const config = await getReCaptchaHeaderTBR('put', endpoint);

      const original = rootState.user.resSelectedForUpdate;
      const updated = rootState.user.resDetails?.userInfo ?? {};
      const confirmationRequest = updated.reservationSendConfirmationRequest ?? {};

      if (rootState.user.customerDetail?.customerId) {
        updated.customerId = rootState.user.customerDetail.customerId;
      }

      const args = {
        cancelRequest: {
          appointmentNumber: original.appointmentNumber,
          dateOfBirth: original.dateOfBirth || '',
          isBusinessCustomer: original.isBusinessCustomer ?? false,
          lastName: original.lastName || '',
          organizationName: original.organizationName || ''
        },
        reservationRequest: {
          customerId: updated.customerId || 0,
          dateOfBirth: updated.dateOfBirth || original.dateOfBirth || '',
          firstName: updated.firstName || '',
          isBusinessCustomer: updated.isBusinessCustomer || false,
          isFeeApplicable: original.isFeeApplicable,
          lastName: updated.lastName || '',
          licenseNumber: updated.licenseNumber || '',
          middleName: updated.middleName || '',
          onlineReservationFeeDetail: {},     // no fee detail in update even for road test
          organizationName: updated.organizationName || '',
          plateNumber: updated.plateNumber || '',
          reservationSendConfirmationRequest: {
            email: confirmationRequest.email || '',
            isCancellationConfirmation: false,
            isConsentProvided: confirmationRequest.isConsentProvided || false,
            phone: confirmationRequest.phone || '',
            isModifyRequest: confirmationRequest.isModifyRequest,
          },
          reservationSlotId: rootState.user.reservationSlotId,
          reservationUserIdentifier: rootState.user.reservationUserId,
          visitTypeId: rootState.user.resType,
        }
      };

      const result = await axios.put(endpoint, args, config);
      dispatch('setFinalReservationPropsHelper', result.data);

      // clean up old details user previously selected
      dispatch('user/setStoreProperty', { property: 'resSelectedForUpdate', value: {} }, { root: true });

    } catch (error) {
      console.error('updateReservation...', error);
      // throw error;
    }
  },
  

  /**
   * Deletes (cancels) an existing reservation.
   * @param {object} context
   * @param {object} data See API for required properties.
   * @returns {Promise<number>}
   */
  async deleteReservation(context, data) {
    const endpoint = `${SERVICE_URL}reservation`;
    const config = await getReCaptchaHeaderTBR('delete', endpoint);

    config.data = {
      appointmentNumber: data.appointmentNumber,
      dateOfBirth: data.dateOfBirth,
      isBusinessCustomer: data.isBusinessCustomer,
      lastName: data.lastName,
      organizationName: data.organizationName
    };

    let configData = {};
    const onlineToken = localStorage.getItem('onlineToken');
    if (onlineToken != null && onlineToken != '') {
      configData = {
        headers: {
          Recaptcha: config.headers.Recaptcha,
          'X-Access-Token': onlineToken || '',
        },
        data: config.data,
      };
    } else {
      configData = {
        headers: {
          Recaptcha: config.headers.Recaptcha,
        },
        data: config.data,
      };
    }
    let response = undefined;
    try {
      response = await axios.delete(endpoint, configData);
    } catch (error) {
      console.error(error);
    }
    return response ? response.status : I_AM_A_TEAPOT;
  },

  /**
   * Checks if RIMS db has this customer stored.
   * @param dispatch
   * @param rootState
   * @param nextRoute The next screen to route to.
   * @returns { customerId, cdlRoadTestEligibilityDate } where customer is allowed to make reservation of a
   *          specific service type, e.g. CDL road test, on or after the cdlRoadTestEligibilityDate.
   */
  async checkCustomerEligibility({ dispatch, rootState }, nextRoute) {
    try {
      const endpoint = `${SERVICE_URL}reservation/eligible`;

      const user = rootState.user.resDetails.userInfo;
      const data = {
        firstName: user.firstName,
        licenseNumber: user.licenseNumber,
        dateOfBirth: user.dateOfBirth,
        visitTypeId: rootState.user.resType,
      };
      
      const result = await axios.get(endpoint, { params: data });
      const customer = result.data;
      const addon = { ...rootState.user.resDetails.userInfo, ...customer };
      dispatch('user/setReservationDetails', [{ property: 'userInfo', value: addon }], { root: true });

      /*
      * Note: 😖 RIMS' mix & match requests...
      * Technically, API will NEVER throw status 461 as it is explicitly requested by RIMS to be
      * handled as a frontend validation BUT it 'must' look like part of the business exception
      * with the orange alert banner
      */

      // check cdlRoadTestEligibilityDate against slot date if customer found
      const TZ_ORIGIN = 'America/New_York';   // dates returned from RIMS are always in ET
      // slot dates are in East Coast time
      const slotDate = zonedTimeToUtc(rootState.user.resDetails.serviceDate, TZ_ORIGIN);
      const utcIssueDate = zonedTimeToUtc(customer.cdlRoadTestEligibilityDate, TZ_ORIGIN);
      const isOnOrAfterWait = isEqual(slotDate, utcIssueDate) || isAfter(slotDate, utcIssueDate);

      if (isOnOrAfterWait) {
        if (!window.config.USEMFA || (rootState.user.reservationSlotId > 0 && rootState.user.customerDetail)) {
          appRouter.push({ name: nextRoute });
        } else {
          return isOnOrAfterWait; // Return the customer object for eligibility check
        }
      } else {
        const wd = new Date(utcIssueDate.toISOString());
        const formattedDate = wd.toLocaleDateString('en-US', {
          timeZone: 'UTC',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });

        // fake the network call response to have consistent display error banner
        // TODO: retest after Release 5 or 4.x
        dispatch(
          // ROOT_SET_STATE,
          'user/setStoreProperty',
          {
            property: 'networkResponse',
            value: {
              status: 461,
              message: `Please schedule your reservation on or after ${formattedDate}.`,
            },
          },
          { root: true }
        );

        // let catch block know there is an "error" so that it will do cleanup
        const error = new Error('Special eligibility check failed');
        error.response = { status: 461, message: 'Special eligibility check...' };
        // throw error;
      }
      return customer;
    } catch (error) {
      const user = rootState.user.resDetails.userInfo;
      const clone = { ...user };

      // keep object "clean" by removing backend props from user details on eligibility error
      delete clone.customerCid;   // internal to RIMS 
      delete clone.customerId;    // external to customers
      delete clone.cdlRoadTestEligibilityDate;

      await dispatch('user/setReservationDetails', [{ property: 'userInfo', value: clone }], { root: true });
      console.error('checkCustomerEligibility...', error.response);

      // throw error;
    }
  },

  // ----------------------------------------------------------
  // "Private" Reservation helpers only used within this module
  /**
   * Helper for reservation create and update operations.
   * @param dispatch
   * @param res The response from API call.
   */
  setFinalReservationPropsHelper({ dispatch }, res) {
    dispatch('user/setStoreProperty', { property: 'resAppointmentNum', value: res.appointmentNumber }, { root: true });
    dispatch('user/setStoreProperty', { property: 'resAppointmentBarcode', value: res.barCode }, { root: true });
    dispatch('user/setStoreProperty', { property: 'resAppointmentLocation', value: res.reservationLocation }, { root: true });
    dispatch('user/setStoreProperty', { property: 'resNotificationSent', value: res.reservationSendConfirmationResponse }, { root: true });
  },

  /**
   * Stores payment receipt required fields (for Print screen in Step 5) in resDetails.userInfo, if applicable.
   * @param dispatch
   * @param rootState
   * @param data The onlineReservationFeeDetail object.
     */
  setPaymentReceiptHelper({ dispatch, rootState }, data) {
    if (data && Object.keys(data).length > 0) {
      const paymentReceipt = {
        authorizationAmount: data.authorizationAmount,
        authorizationNumber: data.authorizationNumber,
        feeName: data.feeName,
        feePaidDate: data.feePaidDate,
        paymentMakerFirstName: data.paymentMakerFirstName,
        paymentMakerLastName: data.paymentMakerLastName,
      };
      const extended = { ...rootState.user.resDetails };
      extended.userInfo.paymentReceipt = paymentReceipt;

      dispatch('user/setStoreProperty', { property: 'resDetails', value: extended }, { root: true });
    } else {
      // no fee detail so do nothing
    }
  },

   /**
   * Calls mfa code.
   * @param {object} context
   * @param {object} data
   */
  async setMfaRequest({ dispatch, rootState }, data) {
    try {
      const endpoint = `${SERVICE_URL}reservation/mfarequest`;
      const config = await getReCaptchaHeaderTBR('post', endpoint);
      const reservationUserIdentifier = (data.reservationUserIdentifier ?? rootState.user.reservationUserId) || '';
      const args = {
        mfaType: data.mfaType,
        mfaTypeValue: data.mfaTypeValue
      };
      const url = `${endpoint}?reservationUserIdentifier=${reservationUserIdentifier}`;
      const result = await axios.post(url, args, config);
      dispatch('user/setStoreProperty', { property: 'mfaRequest', value: result.data }, { root: true });
      return result.data;
      
    } catch (error) {
      console.error('setMfaRequest...', error);
      // throw error;
    }
  },

  /**
   * Calls mfa verification code.
   * @param {object} context
   * @param {object} data
   */
  async setVerifyMfaRequest({ dispatch, rootState }, data) {
    try {
      const endpoint = `${SERVICE_URL}reservation/verifymfacode`;
      const config = await getReCaptchaHeaderTBR('post', endpoint);
      
      const headers = {
      'accept': 'application/json',
      'Content-Type': 'application/json',
      }
      const mfaToken = encodeURIComponent(rootState.user.mfaRequest?.mfaToken);
      const reservationUserIdentifier = (data.reservationUserIdentifier ?? rootState.user.reservationUserId) || '';
      const url = `${endpoint}?mfaToken=${mfaToken}&verificationCode=${data.verificationCode}&reservationUserIdentifier=${reservationUserIdentifier}`;
      const result = await axios.post(url, headers, config);
      if (result.status === rootState.user.STATUSES.SUCCESS) {
        dispatch('user/setStoreProperty', { property: 'mfaVerifyRequest', value: result.data }, { root: true });
        return result.data;
      }
    } catch (error) {
      console.error('setVerifyMfaRequest...', error);
      if(data.attemptCount == 3) {
        setTimeout(async () => {
          await dispatch(SLOTS_HOLD_DELETE, null, { root: true });
          appRouter.push({ name: 'Home', query: { expired: true } });
        }, 5000)
      }
      // throw error;
    }
  },

  /**
   * Retrieves user contact information based on user's email, phone number and/or
   * reservation confirmation number.
   * @param {object} context
   * @param {object} data
   * @returns {Promise<*>}
   */
  async getReservationContact({ dispatch, rootState }, data) {
    try {
      const endpoint = `${SERVICE_URL}reservation/getreservationcontact`;
      const config = await getReCaptchaHeaderTBR('post', endpoint);
      const payLoad = {
        appointmentNumber: data.appointmentNumber,
        emailAddress: data.emailAddress,
        phoneNumber: data.phoneNumber
      };

      const result = await axios.post(endpoint, payLoad, config, data);
      if (result.status === rootState.user.STATUSES.SUCCESS) {
        dispatch('user/setStoreProperty', { property: 'customerDetail', value: result.data }, { root: true });
        return result.data;
      }
    } catch (error) {
      console.error('getReservationContact...', error);
      // throw error;
    }
  },

};

const getters = {};

export default {
  namespaced,
  state,
  mutations,
  actions,
  getters,
}
